import { Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { FieldTree, form, required } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { Yritysmuoto } from '@lemon/core-firebase/model/kayttaja'
import { DateService } from '@lemon/core/service/date.service'
import { AsiakasService, LokalisoituYritysmuoto } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import {
  AnnaAsiakkaidenNimitiedotPyynto,
  AnnaAsiakkaidenNimitiedotVastaus,
  AsiakashakuAsiakas,
  AsiakashakuRequest,
  AsiakashakuResponse,
  AsiakkaanNimitiedot,
  EiYhtiomuotoaYhtiomuoto,
} from 'app/_jaettu-lemonator/model/asiakas'
import { KirjanpitajanNimitiedot } from 'app/_jaettu-lemonator/model/kirjanpitaja'
import { LogEntry, LoggingListEntriesRequest, LoggingListEntriesResponse, StructForLogs } from 'app/_jaettu-lemonator/model/logging'
import { BehaviorSubject, Observable, Subject, combineLatest, from, map, switchMap, takeUntil } from 'rxjs'
import { LoggingDataDialog } from './logging-data.dialog'

export enum EventType {
  NAVIGOI = 'navigoi',
  KLIKKASI_TOIMINTOA = 'klikkasi toimintoa',
}
interface HakuLomake {
  start: Date | null
  end: Date | null
  kirjanpitajaUid: string
  eventType: string
  navigationMethod: string
  eventName: string
  haku: string
  yhtiomuoto: Yritysmuoto | EiYhtiomuotoaYhtiomuoto | null
  asiakasAvain: string | null
}

@Component({
  selector: 'app-logging',
  templateUrl: './logging.component.html',
  styleUrls: ['./logging.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LoggingComponent implements OnInit, OnDestroy {
  lomake: FieldTree<HakuLomake>
  private readonly _injector = inject(Injector)

  private ngUnsubscribe: Subject<void> = new Subject<void>()
  inputname = 'asdfewqrphoki' + Math.random()
  kirjanpitajienNimitiedotObservable: Observable<KirjanpitajanNimitiedot[]>
  asiakkaatMapObservable: Promise<Map<string, AsiakkaanNimitiedot>>

  showNavigationStyle: boolean = false
  showAction: boolean = false

  paivitaValidaatiot: any = null
  tulokset: BehaviorSubject<AsiakashakuAsiakas[]> = new BehaviorSubject(null)
  yritysmuodot: LokalisoituYritysmuoto[]

  valittuAsiakas: AsiakashakuAsiakas
  readonly selectedRow = signal<any>(null)

  readonly commonError = signal<string | null>(null)
  readonly loggingEntries = signal<LogEntry[] | undefined>(undefined)
  displayedColumns: string[] = ['kirjanpitajaUid', 'asiakasAvain', 'eventType', 'eventName', 'navigationMethod', 'seen', 'url', 'timestamp', 'insertId']

  constructor(
    private _dateService: DateService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _validationService: FormValidationService,
    private _ladataanService: LadataanService,
    private _errorHandler: ErrorHandler,
    private _firebase: FirebaseLemonator,
    private _asiakasService: AsiakasService,
    private _firebaseLemonator: FirebaseLemonator,
    private _dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.yritysmuodot = this._asiakasService.yritysmuodotJaEiTiedossa

    this.lomake = form(
      signal<HakuLomake>({
        start: this._dateService.lisaaKuukausia(new Date(), -1),
        end: new Date(),
        kirjanpitajaUid: 'all',
        eventType: 'all',
        navigationMethod: 'all',
        eventName: 'all',

        /* Asiakkaan haku */
        haku: '',
        yhtiomuoto: null,
        asiakasAvain: 'all',
      }),
      polku => {
        required(polku.start)
        required(polku.end)
      },
      { injector: this._injector },
    )

    this.kirjanpitajienNimitiedotObservable = this._kirjanpitajaService.kirjanpitajienNimitiedotObservable.pipe(
      map(kirjanpitajat => {
        if (kirjanpitajat?.length) {
          return kirjanpitajat.sort((a, b) => {
            if (a.etunimi !== b.etunimi) {
              return a.etunimi.localeCompare(b.etunimi)
            }
            return a.sukunimi.localeCompare(b.sukunimi)
          })
        }
        return []
      }),
    )

    this.asiakkaatMapObservable = this._firebaseLemonator
      .functionsCall<AnnaAsiakkaidenNimitiedotPyynto, AnnaAsiakkaidenNimitiedotVastaus>('annaAsiakkaidenNimitiedot', {}, { timeout: 540 * 1000 })
      .then(asiakkaatVastaus => {
        const m: Map<string, AsiakkaanNimitiedot> = new Map()
        if (asiakkaatVastaus?.nimitiedot) {
          for (const a of asiakkaatVastaus.nimitiedot) {
            m.set(a.avain, a)
          }
        }
        return m
      })
  }

  ngOnDestroy(): void {}

  get asiakasAvain(): FieldTree<string | null> {
    return this.lomake.asiakasAvain
  }

  get haku(): FieldTree<string> {
    return this.lomake.haku
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  onTypeChange(event: any) {
    if (event.value === EventType.NAVIGOI) {
      this.showNavigationStyle = true
      this.showAction = false
    } else if (event.value === EventType.KLIKKASI_TOIMINTOA) {
      this.showNavigationStyle = false
      this.showAction = true
    } else {
      this.showNavigationStyle = false
      this.showAction = false
    }
  }

  /* Hae asiakas */
  async search() {
    if (this.lomake().invalid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      this.paivitaValidaatiot = {}
      return
    }

    this._ladataanService.aloitaLataaminen()

    try {
      const req: AsiakashakuRequest = {}
      if (this.lomake.haku().value()) {
        req.teksti = this.lomake.haku().value()
      }
      if (this.lomake.yhtiomuoto().value()) {
        req.yhtiomuoto = this.lomake.yhtiomuoto().value()
      }
      const resp = await this._firebase.functionsCall<AsiakashakuRequest, AsiakashakuResponse>('asiakasHaeAsiakasta', req, { timeout: 540 * 1000 })
      if (resp.e || !resp.asiakkaat) {
        throw new Error('Error: ' + resp.e)
      }
      resp.asiakkaat.sort((a, b) => {
        const lowera = a.n.toLowerCase()
        const lowerb = b.n.toLowerCase()
        if (lowera !== lowerb) {
          return lowera.localeCompare(lowerb)
        }
        return a.y.localeCompare(b.y)
      })
      this.tulokset.next(resp.asiakkaat)
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  chooseCustomerForSearch(asiakas: AsiakashakuAsiakas) {
    const currentSelection = this.lomake.asiakasAvain().value()
    if (currentSelection === asiakas.k) {
      asetaArvo(this.lomake.asiakasAvain, null)
      this.valittuAsiakas = null
    } else {
      asetaArvo(this.lomake.asiakasAvain, asiakas.k)
      this.valittuAsiakas = asiakas
    }
  }

  /** Korjaa mat selectin ongelman */
  compareFn(): boolean {
    return true
  }

  /* Hae logit Google Cloud logging bucketista */
  async haeLogit() {
    if (this.lomake.asiakasAvain().value() === 'search') {
      alert('Valitse asiakas ennen hakua.')
      return
    }

    const start = this._dateService.paivanAlku(this.lomake.start().value())
    const end = this._dateService.paivanLoppu(this.lomake.end().value())
    const startDate = start.toISOString()
    const endDate = end.toISOString()

    const suodatusParametrit: LoggingListEntriesRequest = {
      start: startDate,
      end: endDate,
    }

    const optionalFilterFields = ['kirjanpitajaUid', 'asiakasAvain', 'eventType', 'navigationMethod'] as const

    for (const field of optionalFilterFields) {
      const value = this.lomake[field]().value()
      if (value !== 'all') {
        suodatusParametrit[field] = value
      }
    }

    try {
      this.commonError.set('')
      this._ladataanService.aloitaLataaminen()

      const vastaus = await this._firebaseLemonator.functionsCall<LoggingListEntriesRequest, LoggingListEntriesResponse>('getLoggingEntriesCloudLogging', suodatusParametrit)
      this.loggingEntries.set(vastaus.entries || [])

      combineLatest([from(this.asiakkaatMapObservable), this.kirjanpitajienNimitiedotObservable])
        .pipe(
          takeUntil(this.ngUnsubscribe),
          switchMap(([asiakkaatMap, kirjanpitajat]) => {
            return from(Promise.all(this.loggingEntries().map(entry => this.formatLogEntry(entry, asiakkaatMap, kirjanpitajat))))
          }),
        )
        .subscribe(loggingEntries => {
          this.loggingEntries.set(loggingEntries)

          if (this.loggingEntries().length === 0) {
            this.commonError.set('Ei tuloksia annetuilla hakuehdoilla')
          }
          this._ladataanService.lopetaLataaminen()
        })
    } catch (err) {
      this._ladataanService.lopetaLataaminen()
      this.commonError.set('Haussa tapahtui virhe.')
      this._errorHandler.handleError(new Error('Failed to retrieve logs data: ' + (err?.message || '')))
    }
  }

  async formatLogEntry(entry: LogEntry, asiakkaatMap: Map<string, any>, kirjanpitajat: any[]): Promise<any> {
    const jsonPayload = entry.jsonPayload as StructForLogs

    const asiakasAvain = jsonPayload.asiakasAvain?.stringValue
    const kirjanpitajaUid = jsonPayload.kirjanpitajaUid?.stringValue
    const eventType = jsonPayload.navigationEvent?.structValue?.fields?.eventType?.stringValue || ''
    const navigationMethod = jsonPayload.navigationEvent?.structValue?.fields?.navigationMethod?.stringValue || ''
    const seen = jsonPayload.navigationEvent?.structValue?.fields?.seen?.stringValue || ''
    const eventName = jsonPayload.navigationEvent?.structValue?.fields?.eventName?.stringValue || ''
    const url = jsonPayload.url?.stringValue || ''

    const asiakas = asiakkaatMap.get(asiakasAvain)
    const kirjanpitaja = kirjanpitajat.find(k => k.avain === kirjanpitajaUid)

    const logLink = this.getLogLinkCloudLogging(entry.insertId)

    return {
      ...entry,
      asiakasAvain,
      asiakasNimi: asiakas ? `${asiakas.nimi}` : 'Ei asiakasta/Tuntematon',
      kirjanpitajaNimi: kirjanpitaja ? `${kirjanpitaja.etunimi} ${kirjanpitaja.sukunimi}` : 'Tuntematon',
      eventType,
      navigationMethod,
      seen,
      eventName,
      url,
      logLink,
    }
  }

  getLogLinkCloudLogging(insertId: string) {
    return (
      'https://console.cloud.google.com/logs/query;query=insertId%3D%22' +
      insertId +
      '%22;storageScope=storage,projects%2Feu-lemonator%2Flocations%2Feurope-west1%2Fbuckets%2Faudit-trail%2Fviews%2F_AllLogs;duration=P30D?project=eu-lemonator'
    ) // huom! linkki ei suoraan se mitä GCL tarjoaa "Copy link"
  }

  openCustomerLink(asiakasAvain: string, event: Event) {
    event.preventDefault()
    event.stopPropagation()
    if (asiakasAvain) {
      window.open('https://lemonator.lemontree.fi/asiakkaat/' + asiakasAvain + '/asiakastiedot', '_blank')
    }
  }

  openUrl(link: string, event: Event) {
    event.preventDefault()
    event.stopPropagation()
    window.open('https://lemonator.lemontree.fi/' + link)
  }

  katsoTiedot(rivi: any) {
    this.selectedRow.set(rivi)

    const dialogRef = this._dialog.open(LoggingDataDialog, {
      data: rivi,
    })

    dialogRef.afterClosed().subscribe(() => {
      this.selectedRow.set(null)
    })
  }
}
